<template>
	<div class="modal-card accrual-viewer">
		<header class="modal-card-head has-background-white headerAccrual">
			<div class="startAccrual">
				<div class="calendarHeader">
					<Calendar class="svgIcon" />
				</div>
				<span class="titleAccrual ml-2">
					{{ titleModal }}
					<br>
					<span class="subtitleAccrual">{{ subTitleModal }}</span>
				</span>
			</div>
			<button class="delete" aria-label="Cerrar Modal" @click="close"></button>
		</header>

		<section class="modal-card-body box-body pt-1 px-5 pb-4">
			<p class="titleSection">Devengo del documento</p>
			<p>
				<span v-if="docNumber">Doc. {{ docNumber }}. </span>
				Distribución del
				<strong>
					<span v-if="documentType == 'P'">Egreso</span>
					<span v-else-if="documentType == 'C'">Ingreso</span>
					<span v-else>monto</span>
				</strong>
				en tu Estado de Resultados según el mes devengado.
			</p>

			<div class="mt-3 pt-4">
				<table class="tableAccrual is-fullwidth is-bordered">
					<colgroup>
						<col style="width: 35%;">
						<col style="width: 25%;">
						<col style="width: 15%;">
						<col style="width: 25%;">
					</colgroup>
					<thead>
						<tr>
							<th class="cellHead py-3 px-2" colspan="1">Cuenta</th>
							<th class="cellHead py-3 px-2" colspan="1">Mes Devengo</th>
							<th class="cellHead py-3 px-2 ta-right" colspan="1">%</th>
							<th class="cellHead py-3 px-2 ta-right" colspan="1">Monto (CLP)</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(row, iRow) in rows" :key="iRow">
							<td class="py-4 px-2 is-capitalized maxColumnWidth" :title="row.account">
								{{ row.account }}
							</td>
							<td class="py-4 px-2 is-capitalized">{{ row.month }}</td>
							<td class="py-4 px-2 ta-right">{{ row.percentage }}%</td>
							<td class="py-4 px-2 ta-right">{{ money(row.amount) }}</td>
						</tr>
						<tr v-if="rows.length == 0">
							<td class="py-4 px-2 empty" colspan="4">
								Este documento no tiene devengo.
							</td>
						</tr>
					</tbody>
					<tfoot v-if="rows.length > 0">
						<tr>
							<td class="py-3 px-2 totalCell" colspan="2">Total</td>
							<td class="py-3 px-2 ta-right totalCell">{{ totalPercentage }}%</td>
							<td class="py-3 px-2 ta-right totalCell">{{ money(totalAmount) }}</td>
						</tr>
					</tfoot>
				</table>
			</div>
		</section>

		<footer class="modal-card-foot has-background-white is-justify-content-flex-end">
			<b-button @click="close" class="button is-primary backColor" role="button">
				Cerrar
			</b-button>
		</footer>
	</div>
</template>

<script>
import Calendar from "@/components/svg/Calendar.vue";
import { parseToMoney } from "@/utils/currency";
import moment from "moment";
import "moment/locale/es";

// Visualizador read-only del devengo de un documento del Libro Mayor (ERP).
// Recibe las filas de Accrual ya resueltas (cuenta × mes × %) y las muestra como
// una tabla plana. No edita: el mayor es solo lectura. Los % se muestran como
// enteros (estándar del proyecto); el Monto se computa con el % EXACTO para que
// la suma cuadre con el total del doc (% = parte del total del documento).
// Estilos espejados de ChangeAccrual.vue para mantener el diseño congruente.
export default {
	name: "AccrualViewer",
	components: {
		Calendar,
	},
	props: {
		titleModal: { type: String, default: "" },
		subTitleModal: { type: String, default: "" },
		docNumber: { type: String, default: "" },
		documentType: { type: String, default: "" },
		docAmount: { type: Number, default: 0 },
		// [{ month_year: 'YYYY-MM-DD', percentage: '33.333', account_plan: { level_3 } }]
		accruals: { type: Array, default: () => [] },
	},
	computed: {
		rows() {
			return this.accruals.map((accrual) => {
				const pct = Number(accrual.percentage) || 0;
				const account = accrual.account_plan && accrual.account_plan.level_3
					? accrual.account_plan.level_3
					: "—";
				return {
					account,
					month: this.formatMonth(accrual.month_year),
					percentage: Math.round(pct),
					amount: Math.round((this.docAmount * pct) / 100),
				};
			});
		},
		totalPercentage() {
			// Suma de los % EXACTOS y luego redondeo, no la suma de los enteros
			// por fila: así el total lee 100% aunque las filas redondeadas sumen
			// 99/101 (p.ej. 33.334/33.333/33.333 → 33+33+33 = 99 por fila).
			const sum = this.accruals.reduce(
				(acc, accrual) => acc + (Number(accrual.percentage) || 0),
				0,
			);
			return Math.round(sum);
		},
		totalAmount() {
			return this.rows.reduce((sum, row) => sum + row.amount, 0);
		},
	},
	methods: {
		money(value) {
			return parseToMoney(value);
		},
		formatMonth(monthYear) {
			if (!monthYear) {
				return "—";
			}
			// `.locale("es")` explícito (convención del repo: no depender del
			// default global de moment); `MMM` en es trae punto ("ene."), se quita.
			const formatted = moment(monthYear, "YYYY-MM-DD")
				.locale("es")
				.format("MMM YYYY")
				.replace(".", "");
			return formatted.charAt(0).toUpperCase() + formatted.slice(1);
		},
		close() {
			this.$emit("close");
		},
	},
};
</script>

<style lang="scss" scoped>
* {
	font-family: Inter;
}

// Caso extremo (N filas de devengo): el card se acota al viewport y el body
// scrollea; header y footer quedan siempre visibles. No hay dropdowns acá,
// así que el scroll interno no recorta nada.
.modal-card {
	overflow: hidden;
	max-height: calc(100vh - 48px);
	z-index: 10;
	margin: auto !important;
}

.headerAccrual {
	display: flex;
	border-bottom: none;

	.startAccrual {
		display: flex;

		.calendarHeader {
			width: 48px;
			height: 48px;
			padding: 14px;
			justify-content: center;
			align-items: center;
			border-radius: 24px;
			background: #DCFAE6;

			.svgIcon {
				filter: invert(36%) sepia(48%) saturate(5838%) hue-rotate(140deg) brightness(93%) contrast(94%);
			}
		}

		.titleAccrual {
			flex: 1;
			color: #101828;
			font-size: 14px;
			font-style: normal;
			font-weight: 500;
			line-height: 24px;
			text-transform: uppercase;

			.subtitleAccrual {
				color: #475467;
				font-weight: 400;
			}
		}
	}

	.delete {
		margin-left: auto;
	}
}

.titleSection {
	font-weight: 600;
	font-size: 18px;
	line-height: 28px;
}

.modal-card-body {
	overflow-y: auto;
	min-height: 0;
	overscroll-behavior: contain;
}

.tableAccrual {
	border-collapse: collapse;
	border-radius: 8px;
	border-style: hidden;
	box-shadow: 0 0 0 1px #EAECF0;
	table-layout: fixed;
	width: 100%;

	td,
	.cellHead {
		border: none;
		padding: auto !important;
	}

	.ta-right {
		text-align: right;
	}

	thead {
		background-color: #EAECF0;

		th {
			// Anclado al tope del scrollport del body — las columnas siguen
			// legibles con cualquier cantidad de filas.
			position: sticky;
			top: 0;
			z-index: 2;
			background-color: #EAECF0;
			font-size: 12px;
			font-weight: 500;
			line-height: 18px;
			text-align: left;
			text-underline-position: from-font;
			text-decoration-skip-ink: none;
			color: #475467;
		}
	}

	tbody {
		td {
			font-size: 12px;
			font-weight: 500;
			line-height: 20px;
			text-align: left;
			text-underline-position: from-font;
			text-decoration-skip-ink: none;
			color: #475467;
		}
	}

	tfoot {
		.totalCell {
			// El Total espeja al header: anclado al fondo del scrollport, la
			// cifra que cierra la tabla nunca queda fuera de vista.
			position: sticky;
			bottom: 0;
			z-index: 2;
			background-color: #F9FAFB;
			font-size: 12px;
			font-weight: 600;
			line-height: 20px;
			color: #101828;
			border-top: 1px solid #EAECF0;
		}
	}

	.empty {
		text-align: center;
		color: #475467;
	}

	.maxColumnWidth {
		max-width: 160px;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		cursor: default;
	}
}

.backColor {
	background-color: #00A9AD;
	border: 1px solid #00A9AD;
	box-shadow: 0px 1px 2px 0px #1018280D;
	color: #ffffff;
}
</style>
